<template>
  <div>
    <v-card class="mb-6">
      <v-card-title>Process configurés</v-card-title>
      <v-row>
        <v-col>
          <v-data-table
            class="ml-6 mr-6"
            :page="pagination.options.page"
            :pageCount="pagination.numberOfPages"
            :headers="headers"
            :items="pagination.items"
            :options.sync="pagination.options"
            :server-items-length="pagination.total"
            :loading="page.loading"
            :disable-filtering="true"
            :disable-sort="true"
            :footer-props="{
              'items-per-page-options': [10, 25, 50],
            }"
            :items-per-page="pagination.options.itemsPerPage"
          >
            <template v-slot:item.entry="{ item }">
              <div class="truncateCell">
                {{ item.entry }}
              </div>
            </template>
            <template v-slot:item.enabled="{ item }">
              <v-icon color="success" v-if="item.enabled">
                {{ icons.mdiCheck }}
              </v-icon>
            </template>
            <template v-slot:item.edit="{ item }">
              <div>
                <v-btn
                  class="mx-2"
                  elevation="0"
                  fab
                  small
                  @click="editConfig(item)"
                >
                  <v-icon color="primary">
                    {{ icons.mdiPencil }}
                  </v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  elevation="0"
                  fab
                  small
                  @click="askRemoveConfig(item)"
                >
                  <v-icon color="secondary">
                    {{ icons.mdiTrashCan }}
                  </v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog v-model="dialogModel.edit" max-width="600px">
      <v-card>
        <v-card-title>Modifier la configuration</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="9">
              <v-text-field
                label="Nom de la configuration"
                v-model="page.configToUpdate.name"
                outlined
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-switch
                label="Activée"
                v-model="page.configToUpdate.enabled"
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="page.configToUpdate.site.name"
                label="Site"
                outlined
                hide-details
                disabled
              />
            </v-col>
            <v-col cols="12">
              <time-range-filter
                :initialValue="page.configToUpdate.nhours"
                @valueChanged="updateTimeRange"
                :noUrl="true"
              />
            </v-col>
            <v-col cols="12">
              <score-filter
                :initialValue="page.configToUpdate.min_score"
                @valueChanged="updateScore"
                :noUrl="true"
              />
            </v-col>
            <v-col cols="12">
              <frequency-filter
                :initialValue="page.configToUpdate.frequency_id"
                :forceSiteId="page.configToUpdate.site.id"
                @valueChanged="updateFrequency"
                :noUrl="true"
              />
            </v-col>
          <v-col cols="12">
            <v-select
              v-model="page.configToUpdate.nb_articles_max"
              :items="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
              label="Nb. d'articles générés max"
              outlined
              hide-details
            />
          </v-col>
            <v-col cols="12">
              <forced-category-filter
                :initialValue="page.configToUpdate.forced_category_id"
                :forceSiteId="page.configToUpdate.site.id"
                @valueChanged="updateForcedCategory"
              />
            </v-col>
            <v-col cols="12">
              <wp-author-filter
                :initialValue="page.configToUpdate.author_id"
                :forceSiteId="page.configToUpdate.site.id"
                @valueChanged="updateAuthor"
              />
            </v-col>
            <v-col cols="12">
              <config-filter
                label="Configurations de sources"
                :site="page.configToUpdate.site.id"
                :initialValue="page.configToUpdate.config_ids"
                @valueChanged="updateConfigList"
                :noUrl="true"
                :zindex="zIndex.modale"
              />
            </v-col>
            <v-col cols="12">
              <custom-prompt-template-filter
                :initialValue="
                  page.configToUpdate.custom_prompt_template_id ||
                  (page.configToUpdate.custom_prompt_template &&
                    page.configToUpdate.custom_prompt_template.id) ||
                  0
                "
                :forceSiteId="page.configToUpdate.site.id"
                @valueChanged="updateCustomPromptTemplate"
                :noUrl="true"
              />
            </v-col>
            <v-col cols="12">
              <status-filter
                :initialValue="page.configToUpdate.status_id"
                @valueChanged="updateStatus"
                :noUrl="true"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-row class="w-100" justify="center" align="center">
            <v-col cols="auto">
              <v-btn color="primary" @click="updateConfig">Enregistrer</v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn color="secondary" @click="cancelUpdateConfig"
                >Annuler</v-btn
              >
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="page.showSuccessMessage"
      color="success"
      timeout="3000"
      top
    >
      Modification enregistrée
    </v-snackbar>

    <confirm-modal 
      :open="dialogModel.confirm"
      :question="`Voulez-vous réellement supprimer la configuration '${page.configToUpdate.name}' ?`" 
      @cancel="dialogModel.confirm = false" 
      @confirm="removeConfig" />
  </div>
</template>

<script>
import { computed, onMounted, reactive, watch } from "@vue/composition-api";
import { mdiCheck, mdiPencil, mdiTrashCan } from "@mdi/js";

import ConfirmModal from "@/components/common/modals/Confirm";
import ScoreFilter from "@/components/ai-factory/common/filters/ScoreFilter.vue";
import ConfigFilter from "@/components/news-radar/common/filters/ConfigFilter.vue";
import CustomPromptTemplateFilter from "@/components/ai-factory/full-auto/filters/CustomPromptTemplateFilter.vue";
import ForcedCategoryFilter from "@/components/ai-factory/full-auto/filters/ForcedCategoryFilter.vue";
import FrequencyFilter from "@/components/ai-factory/full-auto/filters/FrequencyFilter.vue";
import StatusFilter from "@/components/ai-factory/full-auto/filters/StatusFilter.vue";
import TimeRangeFilter from "@/components/ai-factory/full-auto/filters/TimeRangeFilter.vue";
import WpAuthorFilter from "@/components/ai-factory/full-auto/filters/WpAuthorFilter.vue";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import { formatNewsRadarConfigList, truncate } from "@/utils/formatting";
import { zIndex } from "@/utils/constants";

export default {
  name: "ConfigTable",
  components: {
    ConfigFilter,
    ConfirmModal,
    CustomPromptTemplateFilter,
    ForcedCategoryFilter,
    FrequencyFilter,
    ScoreFilter,
    StatusFilter,
    TimeRangeFilter,
    WpAuthorFilter,
  },
  setup(props, { emit }) {
    const { axiosGet, axiosPut, axiosDelete } = useAxios();
    const { store } = useProxy();

    const page = reactive({
      configToUpdate: {
        id: 0,
        name: "",
        site: { name: "", id: 0 },
        frequency_id: 0,
        config_ids: [],
        min_score: 0,
        status_id: 0,
        nhours: 0,
        nb_articles_max: 3,
        forced_category_id: null,
        author_id: null,
        custom_prompt_template_id: 0,
        enabled: 1,
      },
      forceCall: false,
      loading: false,
      isOptionsInitialized: false,
      showSuccessMessage: false,
    });

    const pagination = reactive({
      numberOfPages: 0,
      total: 0,
      items: [],
      options: {
        page: 1,
        itemsPerPage: 50,
      },
    });

    const dialogModel = reactive({
      edit: false,
      confirm: false,
    });

    // =>  METHODS
    const updateConfigList = (values) => {
      page.configToUpdate.config_ids = values;
    };

    const updateTimeRange = (value) => {
      page.configToUpdate.nhours = value;
    };

    const updateFrequency = (value) => {
      page.configToUpdate.frequency_id = value;
    };

    const updateStatus = (value) => {
      page.configToUpdate.status_id = value;
    };

    const updateScore = (value) => {
      page.configToUpdate.min_score = value;
    };

    const updateCustomPromptTemplate = (value) => {
      page.configToUpdate.custom_prompt_template_id = value;
    };

    const updateForcedCategory = (value) => {
      page.configToUpdate.forced_category_id = value;
    };

    const updateAuthor = (value) => {
      page.configToUpdate.author_id = value;
    };

    const formatResult = (items) => {
      const storeConfigs = store.getters["newsRadar/getConfigList"] || [];
      const allConfigs = [
        ...storeConfigs,
        ...items.flatMap((item) => item.configs || []),
      ];
      return items.map((item) => ({
        ...item,
        configs: item.config_ids
          ? formatNewsRadarConfigList(item.configs, allConfigs)
              .map((config) => config.name)
              .join(", ")
          : "",
      }));
    };

    const getConfigs = async () => {
      if ((applyFilters.value || page.forceCall) && !page.loading) {
        try {
          page.loading = true;

          const {
            data: { total, items },
          } = await axiosGet("/aif-auto-configs/all", {
            ...(site.value ? { site_id: site.value } : {}),
            process_type: "source",
            page: pagination.options.page,
            limit: pagination.options.itemsPerPage,
          });

          pagination.total = total;
          pagination.items = formatResult(items);
        } catch (error) {
          console.error("error while fetching data");
        }

        page.loading = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        store.dispatch("common/updateApplyFilters", false);
      }

      page.forceCall = false;
    };

    const cancelUpdateConfig = async () => {
      dialogModel.edit = false;
    };

    const editConfig = async (item) => {
      page.configToUpdate = {
        ...item,
        config_ids: item.config_ids ? item.config_ids.split(",").map((id) => parseInt(id, 10)) : [],
      };
      dialogModel.edit = true;
    };

    const updateConfig = async () => {
      const data = {
        ...page.configToUpdate,
        site_id: page.configToUpdate.site.id,
        config_ids: page.configToUpdate.config_ids.join(","),
        site_category_id: null,
        custom_prompt_template_id:
          Number(page.configToUpdate.custom_prompt_template_id || 0) > 0
            ? Number(page.configToUpdate.custom_prompt_template_id)
            : null,
        forced_category_id:
          Number(page.configToUpdate.forced_category_id || 0) > 0
            ? Number(page.configToUpdate.forced_category_id)
            : null,
        author_id:
          Number(page.configToUpdate.author_id || 0) > 0
            ? Number(page.configToUpdate.author_id)
            : null,
      };

      try {
        await axiosPut(`/aif-auto-configs/${page.configToUpdate.id}`, data);
      } catch (error) {
        console.error("Error saving UTM:", error);
        alert("Erreur lors de l'update");
      }
      dialogModel.edit = false;
      page.showSuccessMessage = true;
      page.forceCall = true;
      await getConfigs();
    };

    const askRemoveConfig = async (item) => {
      page.configToUpdate = {
        ...item,
        config_ids: item.config_ids.split(",").map((id) => parseInt(id, 10)),
      };
      dialogModel.confirm = true;
    };

    const removeConfig = async () => {
      dialogModel.confirm = false;
      await axiosDelete(`aif-auto-configs/${page.configToUpdate.id}`);

      page.forceCall = true;
      await getConfigs();
    };

    const refresh = async () => {
      page.forceCall = true;
      await getConfigs();
    };

    // =>  COMPUTED
    const applyFilters = computed(
      () => store.getters["common/getApplyFilters"]
    );
    const site = computed(() => store.getters["aiFactory/getSelectedSites"]);

    // =>  WATCH
    watch(applyFilters, async () => {
      pagination.options.page = 1;
      await getConfigs();
    });

    watch(
      () => pagination.options,
      () => {
        if (page.isOptionsInitialized && !applyFilters.value) {
          // Not on initialization : wait for a real user change
          page.forceCall = true;
        }

        page.isOptionsInitialized = true;
        getConfigs();
      }
    );

    // =>  HOOKS
    onMounted(async () => {
      page.forceCall = true;
      await getConfigs();
    });

    // =>  DATA
    return {
      askRemoveConfig,
      cancelUpdateConfig,
      dialogModel,
      editConfig,
      headers: [
        { text: "Nom", value: "name" },
        { text: "Site", value: "site.name" },
        { text: "Configs Sources", value: "configs" },
        { text: "Score min.", value: "min_score" },
        { text: "Plage d'observ.", value: "nhours" },
        { text: "Fréquence", value: "frequency.name" },
        { text: "Nb. art. max", value: "nb_articles_max" },
        { text: "Catégorie forcée", value: "forced_category_name" },
        { text: "Rédacteur", value: "author_name" },
        { text: "Template", value: "custom_prompt_template.name" },
        { text: "Statut", value: "status.name" },
        { text: "Activée", value: "enabled" },
        { text: "Actions", value: "edit" },
      ],
      icons: {
        mdiCheck,
        mdiPencil,
        mdiTrashCan,
      },
      page,
      pagination,
      refresh,
      removeConfig,
      truncate,
      updateAuthor,
      updateConfig,
      updateConfigList,
      updateCustomPromptTemplate,
      updateForcedCategory,
      updateFrequency,
      updateScore,
      updateStatus,
      updateTimeRange,
      zIndex,
    };
  },
};
</script>

<style scoped>
.truncateCell {
  width: 10rem;
  overflow: hidden;
  cursor: pointer;
  overflow-x: scroll;
  white-space: nowrap;
}
</style>
